(() => {
  const SELECTOR = ".sj30-horizontal-tab";
  const CARE_SWIPER_SELECTOR = ".sj30-care-swiper";
  const COLD_PRESS_VIDEO_SELECTOR = ".sj30-cold-press-video-block";
  const HERO_SELECTOR = ".sj30-kv";
  const DURATION = 700;
  let heroPopupEscapeInitialized = false;

  const initHorizontalTabs = () => {
    document.querySelectorAll(SELECTOR).forEach((section) => {
      if (section.dataset.sj30SmoothTabsInitialized === "true") return;

      const swiperElement = section.querySelector(".new-common-product-swiper-container");
      const swiper = swiperElement?.swiper;
      if (!swiper) return;

      section.dataset.sj30SmoothTabsInitialized = "true";
      swiper.params.speed = DURATION;

      section.addEventListener(
        "click",
        (event) => {
          const tab = event.target.closest(".item_featured-item");
          if (!tab || !section.contains(tab)) return;

          const index = Number(tab.dataset.index);
          if (!Number.isInteger(index)) return;

          event.preventDefault();
          event.stopImmediatePropagation();

          const duration = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : DURATION;

          swiper.slideTo(index, duration);

          section.querySelectorAll(".item_featured-item").forEach((item) => {
            const isActive = item === tab;
            item.classList.toggle("active", isActive);
            item.setAttribute("aria-pressed", String(isActive));
          });
        },
        true,
      );
    });
  };

  const initColdPressVideoControls = () => {
    document.querySelectorAll(COLD_PRESS_VIDEO_SELECTOR).forEach((block, blockIndex) => {
      if (block.dataset.sj30VideoControlInitialized === "true") return;

      const button = block.querySelector("[data-sj30-video-toggle]");
      const media = button?.closest(".common-video-content");
      const video =
        block.querySelector(".common-lazy-video") ||
        [...block.querySelectorAll("video")].find((candidate) => !candidate.closest(".pc-video-box, .mb-video-box"));

      if (!button || !video) return;

      block.dataset.sj30VideoControlInitialized = "true";
      block.dataset.sj30VideoButtonAllowsPlayback = "false";
      video.controls = false;
      video.autoplay = false;
      video.removeAttribute("autoplay");
      video.pause();

      if (!video.id) video.id = `sj30-cold-press-video-${blockIndex + 1}`;
      button.setAttribute("aria-controls", video.id);
      button.classList.add("is-ready");

      const syncButtonState = () => {
        const isPlaying = !video.paused && !video.ended;
        button.classList.toggle("is-playing", isPlaying);
        button.classList.toggle("is-paused", !isPlaying);
        button.setAttribute("aria-pressed", String(isPlaying));
        button.setAttribute("aria-label", isPlaying ? "Pause video" : "Play video");
      };

      button.addEventListener("pointerdown", () => {
        button.dataset.sj30PointerActivated = "true";
      });
      button.addEventListener("keydown", () => {
        delete button.dataset.sj30PointerActivated;
      });
      media?.addEventListener("mouseleave", () => {
        if (button.dataset.sj30PointerActivated !== "true") return;
        button.blur();
        delete button.dataset.sj30PointerActivated;
      });

      button.addEventListener("click", async () => {
        if (!video.paused && !video.ended) {
          block.dataset.sj30VideoButtonAllowsPlayback = "false";
          video.pause();
          return;
        }

        block.dataset.sj30VideoButtonAllowsPlayback = "true";
        try {
          await video.play();
        } catch (error) {
          block.dataset.sj30VideoButtonAllowsPlayback = "false";
          syncButtonState();
        }
      });

      video.addEventListener("play", () => {
        if (block.dataset.sj30VideoButtonAllowsPlayback !== "true") {
          video.pause();
          return;
        }
        syncButtonState();
      });
      video.addEventListener("pause", syncButtonState);
      video.addEventListener("ended", () => {
        block.dataset.sj30VideoButtonAllowsPlayback = "false";
        syncButtonState();
      });
      video.addEventListener("loadedmetadata", syncButtonState);

      syncButtonState();
    });
  };

  const pauseHeroPopupVideo = (container) => {
    container?.querySelector("video")?.pause();
  };

  const initHeroPopupVideo = () => {
    document.querySelectorAll(HERO_SELECTOR).forEach((hero) => {
      hero.querySelectorAll(".product-media-iframe-video-btn .play-btn[data-id]").forEach((button) => {
        if (button.dataset.sj30PopupVideoInitialized === "true") return;

        const id = button.dataset.id;
        const container = hero.querySelector(`.product-media-video-container-${id}`);
        const video = container?.querySelector("video");
        if (!container || !video) return;

        button.dataset.sj30PopupVideoInitialized = "true";
        button.addEventListener("click", () => {
          if (video.ended) video.currentTime = 0;
          video.play().catch(() => {});
        });

        container.querySelectorAll(".pmvcib-close-btn").forEach((closeButton) => {
          closeButton.addEventListener("click", () => pauseHeroPopupVideo(container));
        });

        container.addEventListener("click", (event) => {
          if (event.target === container) pauseHeroPopupVideo(container);
        });
      });
    });

    if (heroPopupEscapeInitialized) return;
    heroPopupEscapeInitialized = true;
    document.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      document.querySelectorAll(`${HERO_SELECTOR} .product-media-video-container`).forEach(pauseHeroPopupVideo);
    });
  };

  const initCareSwiperTabs = () => {
    document.querySelectorAll(CARE_SWIPER_SELECTOR).forEach((section) => {
      if (section.dataset.sj30CareTabsInitialized === "true") return;

      const swiperElement = section.querySelector(".new-common-product-swiper-container");
      const swiper = swiperElement?.swiper;
      const tabs = [...section.querySelectorAll("[data-sj30-care-index]")];
      if (!swiper || tabs.length === 0) return;

      section.dataset.sj30CareTabsInitialized = "true";
      swiper.params.speed = DURATION;

      const syncTabs = () => {
        const activeIndex = Number.isInteger(swiper.realIndex) ? swiper.realIndex : swiper.activeIndex;
        tabs.forEach((tab, index) => {
          const isActive = index === activeIndex;
          tab.classList.toggle("is-active", isActive);
          tab.setAttribute("aria-pressed", String(isActive));
        });
      };

      tabs.forEach((tab) => {
        tab.addEventListener("click", () => {
          const index = Number(tab.dataset.sj30CareIndex);
          if (!Number.isInteger(index)) return;

          const duration = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : DURATION;
          swiper.slideTo(index, duration);
          syncTabs();
        });
      });

      swiper.on("slideChange", syncTabs);
      syncTabs();
    });
  };

  if (document.readyState === "complete") {
    initHorizontalTabs();
    initColdPressVideoControls();
    initHeroPopupVideo();
    initCareSwiperTabs();
  } else {
    document.addEventListener(
      "DOMContentLoaded",
      () => {
        initHorizontalTabs();
        initColdPressVideoControls();
        initHeroPopupVideo();
        initCareSwiperTabs();
      },
      { once: true },
    );
  }

  document.addEventListener("shopify:section:load", () => {
    initHorizontalTabs();
    initColdPressVideoControls();
    initHeroPopupVideo();
    initCareSwiperTabs();
  });
})();
